import { type Ref, type RefObject, useEffect, useRef } from 'react';

/**
 * Get a ref object where the ref can potentially be in the function format.
 *
 * @example
 *
 * interface Props {
 *   ref?: Ref<HTMLAnchorElement>;
 *   [...]
 * }
 *
 * function MyLink({ ref, ...props }: Props) {
 *   const linkRef = useRefObject<HTMLAnchorElement>(ref);
 *   useEffect(() => {
 *     console.log('Link points to', linkRef.current?.href);
 *   }, []);
 *   return <a ref={linkRef} {...props}>...</a>;
 * }
 */
export function useRefObject<T>(ref: Ref<T> | undefined): RefObject<T | null> {
	const targetRef = useRef<T>(null);

	useEffect(() => {
		if (!ref) {
			return;
		}

		if (typeof ref === 'function') {
			ref(targetRef.current);
		} else {
			ref.current = targetRef.current;
		}
	}, [ref]);

	return targetRef;
}
